import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Search, SlidersHorizontal, X, DollarSign, Check, FolderOpen } from "lucide-react";
import { SortOption, CollectionInfo } from "@/hooks/useMarketplaceNFTs";
import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface SearchFiltersProps {
  search: string;
  setSearch: (value: string) => void;
  sortBy: SortOption;
  setSortBy: (value: SortOption) => void;
  priceRange: [number, number];
  setPriceRange: (value: [number, number]) => void;
  totalResults: number;
  collections?: CollectionInfo[];
  selectedCollections?: string[];
  setSelectedCollections?: (value: string[]) => void;
}

const sortOptions: { value: SortOption; label: string }[] = [
  { value: "trending", label: "Trending" },
  { value: "recent", label: "Recent" },
  { value: "top_emon", label: "Top EMON" },
  { value: "collection", label: "Collection" },
];

export function SearchFilters({
  search,
  setSearch,
  sortBy,
  setSortBy,
  priceRange,
  setPriceRange,
  totalResults,
  collections = [],
  selectedCollections = [],
  setSelectedCollections,
}: SearchFiltersProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasActiveFilters = search || priceRange[0] > 0 || priceRange[1] < 500 || selectedCollections.length > 0;

  const clearFilters = () => {
    setSearch("");
    setPriceRange([0, 500]);
    setSortBy("trending");
    setSelectedCollections?.([]);
  };

  const toggleCollection = (collectionName: string) => {
    if (!setSelectedCollections) return;
    if (selectedCollections.includes(collectionName)) {
      setSelectedCollections(selectedCollections.filter((c) => c !== collectionName));
    } else {
      setSelectedCollections([...selectedCollections, collectionName]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, owner, or wallet..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-background/50"
          />
          {search && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearch("")}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Sort select */}
        <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
          <SelectTrigger className="w-full sm:w-48 bg-background/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border z-50">
            {sortOptions.map((opt) => (
              <SelectItem 
                key={opt.value} 
                value={opt.value}
                className="text-foreground hover:bg-primary/10 focus:bg-primary/20 data-[state=checked]:bg-primary/10 cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  {sortBy === opt.value && <Check className="w-4 h-4 text-primary" />}
                  <span className={sortBy === opt.value ? "text-primary font-semibold" : "text-foreground"}>
                    {opt.label}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filters sheet (mobile + desktop) */}
        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="sm:hidden gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="h-2 w-2 rounded-full bg-primary" />
              )}
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {/* Mobile-only price range (desktop has inline slider) */}
              <div className="space-y-3 sm:hidden">
                <label className="text-sm font-medium flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Price Range (USD)
                </label>
                <Slider
                  value={priceRange}
                  onValueChange={(v) => setPriceRange(v as [number, number])}
                  min={0}
                  max={500}
                  step={10}
                  className="py-4"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>${priceRange[0]}</span>
                  <span>${priceRange[1]}</span>
                </div>
              </div>

              {/* Collections filter (mobile + desktop) */}
              {collections.length > 0 && (
                <div className="space-y-3">
                  <label className="text-sm font-medium flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" />
                    Collections
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {collections.map((col) => (
                      <Badge
                        key={col.collection_name}
                        variant={selectedCollections.includes(col.collection_name) ? "default" : "outline"}
                        className="cursor-pointer hover:bg-primary/20 transition-colors"
                        onClick={() => toggleCollection(col.collection_name)}
                      >
                        <FolderOpen className="w-3 h-3 mr-1" />
                        {col.collection_name}
                        <span className="ml-1 text-xs opacity-70">({col.nft_count})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  Clear All Filters
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop filters */}
      <div className="hidden sm:flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <label className="text-sm text-muted-foreground whitespace-nowrap flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            Price:
          </label>
          <div className="flex-1 max-w-xs">
            <Slider
              value={priceRange}
              onValueChange={(v) => setPriceRange(v as [number, number])}
              min={0}
              max={500}
              step={10}
            />
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            ${priceRange[0]} - ${priceRange[1]}
          </span>
        </div>

        {/* Collections filter button */}
        {collections.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setFiltersOpen(true)}
          >
            <FolderOpen className="w-4 h-4" />
            {selectedCollections.length > 0
              ? `${selectedCollections.length} collections`
              : "Filter Collections"}
            {selectedCollections.length > 0 && (
              <span className="h-2 w-2 rounded-full bg-primary" />
            )}
          </Button>
        )}

        <div className="text-sm text-muted-foreground">{totalResults} results</div>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="w-3 h-3 mr-1" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
